import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, LogOut, RefreshCw, X, Phone, Mail, MapPin, Calendar, Clock, FileText, Wrench } from "lucide-react";
import { LogoFull } from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "rs_admin_token";

const STATUS_STYLES = {
  pending: "border-[#FFB800]/40 bg-[#FFB800]/10 text-[#FFB800]",
  confirmed: "border-sky-400/40 bg-sky-400/10 text-sky-400",
  completed: "border-[#10B981]/40 bg-[#10B981]/10 text-[#10B981]",
};

const inputCls =
  "w-full rounded-lg border border-white/10 bg-[#0A0B0E] px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none transition-colors focus:border-[#FF2A3B]/60";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [bookings, setBookings] = useState(null);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setBookings(null);
    setSelectedId(null);
  };

  const fetchBookings = async (t) => {
    try {
      const { data } = await axios.get(`${API}/bookings`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setBookings(data);
    } catch (e) {
      if (e.response?.status === 401) {
        logout();
      } else {
        toast.error("Failed to load bookings");
        setBookings([]);
      }
    }
  };

  useEffect(() => {
    if (token) fetchBookings(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      setToken(data.access_token);
      toast.success("Welcome back, dispatcher.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed. Check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(
        `${API}/bookings/${id}`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBookings((prev) => prev.map((b) => (b.id === id ? { ...b, status } : b)));
      toast.success(`Booking marked ${status}`);
    } catch {
      toast.error("Could not update status");
    }
  };

  const filtered = (bookings || []).filter((b) => {
    const q = search.toLowerCase();
    return (
      !q ||
      b.name?.toLowerCase().includes(q) ||
      b.email?.toLowerCase().includes(q) ||
      b.service_label?.toLowerCase().includes(q) ||
      b.address?.toLowerCase().includes(q)
    );
  });

  const selected = (bookings || []).find((b) => b.id === selectedId);

  return (
    <div className="min-h-screen bg-[#0A0B0E] text-slate-100" data-testid="admin-page">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 py-8">
        <div className="flex items-center justify-between">
          <Link to="/" data-testid="admin-logo-link" aria-label="Back to site">
            <LogoFull />
          </Link>
          {token && (
            <div className="flex items-center gap-3">
              <button
                onClick={() => fetchBookings(token)}
                className="flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-sm text-slate-300 transition-colors hover:border-[#FF2A3B]/50"
                data-testid="admin-refresh-button"
              >
                <RefreshCw size={14} /> Refresh
              </button>
              <button
                onClick={logout}
                className="flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-sm text-slate-300 transition-colors hover:border-[#FF2A3B]/50"
                data-testid="admin-logout-button"
              >
                <LogOut size={14} /> Logout
              </button>
            </div>
          )}
        </div>

        {!token ? (
          <div className="mx-auto mt-24 max-w-md">
            <form onSubmit={handleLogin} className="card-surface rounded-xl p-8 space-y-5" data-testid="admin-login-form">
              <div>
                <p className="font-jbmono text-xs uppercase tracking-[0.25em] text-[#FF2A3B] font-semibold">
                  Restricted
                </p>
                <h1 className="mt-2 font-display text-2xl font-bold tracking-tight">
                  Dispatch Console
                </h1>
              </div>
              <input
                type="email"
                required
                placeholder="Admin email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputCls}
                data-testid="admin-email-input"
              />
              <input
                type="password"
                required
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={inputCls}
                data-testid="admin-password-input"
              />
              {error && (
                <p className="text-sm text-[#FF2A3B]" data-testid="admin-login-error">
                  {error}
                </p>
              )}
              <button
                type="submit"
                disabled={loading}
                className="flex w-full items-center justify-center gap-2 rounded-full bg-[#FF2A3B] py-3.5 text-sm font-semibold text-white transition-all hover:bg-[#E01F30] disabled:opacity-60"
                data-testid="admin-login-submit"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : "Sign In"}
              </button>
              <Link to="/" className="block text-center font-jbmono text-xs uppercase tracking-[0.2em] text-slate-500 hover:text-white" data-testid="admin-back-link">
                ← Back to site
              </Link>
            </form>
          </div>
        ) : (
          <div className="mt-10">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
              <div>
                <p className="font-jbmono text-xs uppercase tracking-[0.25em] text-[#FF2A3B] font-semibold">
                  Dispatch Console
                </p>
                <h1 className="mt-2 font-display text-2xl sm:text-3xl font-bold tracking-tight" data-testid="admin-heading">
                  Incoming Bookings
                </h1>
                <p className="mt-1 text-sm text-slate-500">Tap any row for full details and one-tap call back.</p>
              </div>
              <input
                placeholder="Search name, service, address..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className={`${inputCls} sm:max-w-xs`}
                data-testid="admin-search-input"
              />
            </div>

            {bookings === null ? (
              <div className="mt-16 flex justify-center" data-testid="admin-loading">
                <Loader2 size={28} className="animate-spin text-[#FF2A3B]" />
              </div>
            ) : filtered.length === 0 ? (
              <p className="mt-16 text-center text-slate-500" data-testid="admin-empty-state">
                No bookings found.
              </p>
            ) : (
              <div className="mt-8 overflow-x-auto rounded-xl border border-white/10" data-testid="admin-bookings-table">
                <table className="w-full min-w-[900px] text-left text-sm">
                  <thead className="border-b border-white/10 bg-[#12141C]">
                    <tr>
                      {["Requested", "Client", "Service", "Schedule", "Property", "Contact", "Status"].map((h) => (
                        <th key={h} className="px-5 py-4 font-jbmono text-xs uppercase tracking-[0.18em] text-slate-500">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((b) => (
                      <tr
                        key={b.id}
                        onClick={() => setSelectedId(b.id)}
                        className="cursor-pointer border-b border-white/5 transition-colors hover:bg-[#FF2A3B]/[0.04]"
                        data-testid={`admin-booking-row-${b.id.slice(0, 8)}`}
                      >
                        <td className="px-5 py-4 font-jbmono text-xs text-slate-400">
                          {new Date(b.created_at).toLocaleDateString()}
                          <span className="block text-slate-500">#{b.id.slice(0, 8).toUpperCase()}</span>
                        </td>
                        <td className="px-5 py-4 font-medium text-white">{b.name}</td>
                        <td className="px-5 py-4 text-slate-300">{b.service_label}</td>
                        <td className="px-5 py-4 text-slate-300">
                          {b.preferred_date}
                          <span className="block text-xs text-slate-500">{b.time_slot_label}</span>
                        </td>
                        <td className="px-5 py-4 text-slate-300">
                          <span className="capitalize">{b.property_type}</span>
                          <span className="block max-w-[200px] truncate text-xs text-slate-500">{b.address}</span>
                        </td>
                        <td className="px-5 py-4 text-xs text-slate-400">
                          {b.email}
                          <span className="block">{b.phone}</span>
                        </td>
                        <td className="px-5 py-4">
                          <select
                            value={b.status}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => updateStatus(b.id, e.target.value)}
                            className={`rounded-full border px-3 py-1.5 font-jbmono text-xs uppercase tracking-[0.15em] outline-none bg-[#0A0B0E] ${STATUS_STYLES[b.status] || ""}`}
                            data-testid={`admin-booking-status-select-${b.id.slice(0, 8)}`}
                          >
                            <option value="pending">Pending</option>
                            <option value="confirmed">Confirmed</option>
                            <option value="completed">Completed</option>
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      <AnimatePresence>
        {selected && (
          <>
            <motion.div
              key="backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedId(null)}
              className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
              data-testid="admin-detail-backdrop"
            />
            <motion.aside
              key="panel"
              initial={{ x: 480 }}
              animate={{ x: 0 }}
              exit={{ x: 480 }}
              transition={{ type: "spring", damping: 32, stiffness: 320 }}
              className="fixed right-0 top-0 z-50 h-full w-full max-w-md overflow-y-auto border-l border-white/10 bg-[#12141C] p-8"
              data-testid="admin-detail-panel"
            >
              <div className="flex items-start justify-between">
                <p className="font-jbmono text-xs uppercase tracking-[0.25em] text-[#FF2A3B] font-semibold">
                  Booking #{selected.id.slice(0, 8).toUpperCase()}
                </p>
                <button
                  onClick={() => setSelectedId(null)}
                  className="rounded-full border border-white/10 p-2 text-slate-400 transition-colors hover:border-[#FF2A3B]/50 hover:text-white"
                  data-testid="admin-detail-close-button"
                  aria-label="Close details"
                >
                  <X size={16} />
                </button>
              </div>

              <h2 className="mt-4 font-display text-2xl font-bold tracking-tight" data-testid="admin-detail-name">
                {selected.name}
              </h2>
              <span className="mt-2 inline-block rounded-full border border-white/10 px-3 py-1 font-jbmono text-xs uppercase tracking-[0.2em] text-slate-400">
                {selected.property_type}
              </span>

              <div className="mt-6">
                <label className="mb-2 block font-jbmono text-xs uppercase tracking-[0.2em] text-slate-500">
                  Status
                </label>
                <select
                  value={selected.status}
                  onChange={(e) => updateStatus(selected.id, e.target.value)}
                  className={`w-full rounded-lg border px-4 py-3 font-jbmono text-xs uppercase tracking-[0.15em] outline-none bg-[#0A0B0E] ${STATUS_STYLES[selected.status] || ""}`}
                  data-testid="admin-detail-status-select"
                >
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="completed">Completed</option>
                </select>
              </div>

              <div className="mt-6 space-y-3">
                <a
                  href={`tel:${selected.phone}`}
                  className="flex w-full items-center justify-center gap-2 rounded-full bg-[#FF2A3B] py-4 text-sm font-semibold text-white transition-all hover:bg-[#E01F30] hover:shadow-[0_0_30px_rgba(255,42,59,0.35)]"
                  data-testid="admin-detail-call-button"
                >
                  <Phone size={16} />
                  Call back — {selected.phone}
                </a>
                <a
                  href={`mailto:${selected.email}?subject=Your Red Star Solutions booking ${selected.id.slice(0, 8).toUpperCase()}`}
                  className="flex w-full items-center justify-center gap-2 rounded-full border border-white/15 py-4 text-sm font-semibold text-slate-200 transition-colors hover:border-[#FF2A3B]/60"
                  data-testid="admin-detail-email-button"
                >
                  <Mail size={16} />
                  Email — {selected.email}
                </a>
              </div>

              <div className="mt-8 space-y-4">
                <div className="flex items-start gap-3" data-testid="admin-detail-service">
                  <Wrench size={15} className="mt-0.5 shrink-0 text-[#FF2A3B]" />
                  <div>
                    <p className="font-jbmono text-xs uppercase tracking-[0.2em] text-slate-500">Service</p>
                    <p className="mt-0.5 text-sm text-white">{selected.service_label}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-testid="admin-detail-date">
                  <Calendar size={15} className="mt-0.5 shrink-0 text-[#FF2A3B]" />
                  <div>
                    <p className="font-jbmono text-xs uppercase tracking-[0.2em] text-slate-500">Preferred date</p>
                    <p className="mt-0.5 text-sm text-white">{selected.preferred_date}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-testid="admin-detail-slot">
                  <Clock size={15} className="mt-0.5 shrink-0 text-[#FF2A3B]" />
                  <div>
                    <p className="font-jbmono text-xs uppercase tracking-[0.2em] text-slate-500">Time window</p>
                    <p className="mt-0.5 text-sm text-white">{selected.time_slot_label}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-testid="admin-detail-address">
                  <MapPin size={15} className="mt-0.5 shrink-0 text-[#FF2A3B]" />
                  <div>
                    <p className="font-jbmono text-xs uppercase tracking-[0.2em] text-slate-500">Property address</p>
                    <p className="mt-0.5 text-sm text-white">{selected.address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Calendar size={15} className="mt-0.5 shrink-0 text-slate-500" />
                  <div>
                    <p className="font-jbmono text-xs uppercase tracking-[0.2em] text-slate-500">Requested</p>
                    <p className="mt-0.5 text-sm text-slate-400">{new Date(selected.created_at).toLocaleString()}</p>
                  </div>
                </div>
              </div>

              <div className="mt-8 rounded-xl border border-white/10 bg-[#0A0B0E] p-5" data-testid="admin-detail-notes">
                <div className="flex items-center gap-2">
                  <FileText size={14} className="text-[#FF2A3B]" />
                  <p className="font-jbmono text-xs uppercase tracking-[0.2em] text-slate-500">Customer notes</p>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-slate-300">
                  {selected.notes || "No notes provided."}
                </p>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
